import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export interface Portfolio {
  id: string;
  title: string;
  price: number;
  location: string;
  propertyType: string;
  authorityStatus: string;
  photo: string | null;
  photos: number;
  documents: number;
  viewCount: number;
  status: string;
  createdAt: string;
}

interface DbRow {
  id: string;
  title: string;
  price: number;
  location: string | null;
  property_type: string | null;
  authority_status: string | null;
  photo: string | null;
  photos: number | null;
  documents: number | null;
  view_count: number | null;
  status: string | null;
  created_at: string;
}

function mapDb(row: DbRow): Portfolio {
  return {
    id: row.id,
    title: row.title,
    price: Number(row.price) || 0,
    location: row.location || '',
    propertyType: row.property_type || '',
    authorityStatus: row.authority_status || 'Açık',
    photo: row.photo,
    photos: row.photos || 0,
    documents: row.documents || 0,
    viewCount: row.view_count || 0,
    status: row.status || 'Aktif',
    createdAt: row.created_at ? row.created_at.split('T')[0] : '',
  };
}

function computeStats(items: Portfolio[]) {
  const totalActive = items.filter((p) => p.status === 'Aktif').length;
  const soldItems = items.filter((p) => p.status === 'Satıldı');
  const totalSold = soldItems.length;
  const avgSaleDays =
    soldItems.length > 0
      ? Math.round(
          soldItems.reduce(
            (sum, p) =>
              sum +
              Math.max(
                0,
                Math.floor(
                  (new Date().getTime() - new Date(p.createdAt).getTime()) /
                    (1000 * 60 * 60 * 24)
                )
              ),
            0
          ) / soldItems.length
        )
      : 0;
  const totalValue = items.reduce((s, p) => s + p.price, 0);
  return { totalActive, totalSold, avgSaleDays, totalValue };
}

export function usePortfolios() {
  const { user } = useAuth();
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [stats, setStats] = useState({ totalActive: 0, totalSold: 0, avgSaleDays: 0, totalValue: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Recalculate stats whenever portfolios change
  useEffect(() => {
    setStats(computeStats(portfolios));
  }, [portfolios]);

  const fetchPortfolios = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);

    const { data, error: dbError } = await supabase
      .from('ep_portfolios')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (dbError) {
      setError(dbError.message);
      setIsLoading(false);
      return;
    }

    const items = (data || []).map(mapDb);
    setPortfolios(items);
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  const addPortfolio = useCallback(
    async (form: {
      title: string;
      price: number;
      location: string;
      propertyType: string;
      authorityStatus: string;
      photo?: string;
    }): Promise<{ error: string | null; portfolio?: Portfolio }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const dbRow = {
        user_id: user.id,
        title: form.title,
        price: form.price,
        location: form.location,
        property_type: form.propertyType,
        authority_status: form.authorityStatus,
        photo: form.photo || null,
        photos: 0,
        documents: 0,
        view_count: 0,
        status: 'Aktif',
      };

      const { data, error: dbError } = await supabase
        .from('ep_portfolios')
        .insert(dbRow)
        .select()
        .single();

      if (dbError || !data) {
        return { error: dbError?.message || 'Ekleme başarısız oldu.' };
      }

      const newItem = mapDb(data as DbRow);
      setPortfolios((prev) => [newItem, ...prev]);
      return { error: null, portfolio: newItem };
    },
    [user]
  );

  const updatePortfolio = useCallback(
    async (
      id: string,
      updates: Partial<Portfolio>
    ): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const dbUpdates: Record<string, unknown> = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.price !== undefined) dbUpdates.price = updates.price;
      if (updates.location !== undefined) dbUpdates.location = updates.location;
      if (updates.propertyType !== undefined) dbUpdates.property_type = updates.propertyType;
      if (updates.authorityStatus !== undefined) dbUpdates.authority_status = updates.authorityStatus;
      if (updates.photo !== undefined) dbUpdates.photo = updates.photo;
      if (updates.status !== undefined) dbUpdates.status = updates.status;

      const { error: dbError } = await supabase
        .from('ep_portfolios')
        .update(dbUpdates)
        .eq('id', id)
        .eq('user_id', user.id);

      if (dbError) {
        return { error: dbError.message };
      }

      setPortfolios((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)));
      return { error: null };
    },
    [user]
  );

  const deletePortfolio = useCallback(
    async (id: string): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const { error: dbError } = await supabase
        .from('ep_portfolios')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (dbError) {
        return { error: dbError.message };
      }

      setPortfolios((prev) => prev.filter((p) => p.id !== id));
      return { error: null };
    },
    [user]
  );

  return {
    portfolios,
    stats,
    isLoading,
    error,
    refresh: fetchPortfolios,
    addPortfolio,
    updatePortfolio,
    deletePortfolio,
  };
}